import {
  useGetFailedHolidayJobsQuery,
  useGetFailedHolidayListQuery,
  useLazyDownloadHolidayTemplateQuery,
  useUploadHolidayMutation,
} from '@/app/features/employee/emp-leave/emp-leave.api';
import type { FailedHolidayListResponse } from '@/app/features/employee/emp-leave/emp-leave.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  data: any;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
};

type HolidayRecord = FailedHolidayListResponse['data']['data'][0];

const columns: TableProps<HolidayRecord>['columns'] = [
  {
    title: 'Name',
    dataIndex: 'holidayData',
    render: (val) => val?.Name ?? 'n/a',
  },
  {
    title: 'Date',
    dataIndex: 'holidayData',
    render: (val) => val?.['Date (YYYY-MM-DD)'] ?? 'n/a',
  },
  {
    title: 'Type',
    dataIndex: 'holidayData',
    render: (val) => val?.Type ?? 'n/a',
  },
  {
    title: 'Description',
    dataIndex: 'holidayData',
    render: (val) => val?.Description ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failureReason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function UploadCalendar({ open, handleClose }: Props) {
  const [uploadHoliday, { isLoading: isUploading }] = useUploadHolidayMutation();
  const [downloadTemplate] = useLazyDownloadHolidayTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'holiday-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadHoliday(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Calendar CSV Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<HolidayRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedHolidayJobsQuery}
        useGetRecordsQuery={useGetFailedHolidayListQuery}
        recordsFieldMap={{ total: 'totalLength' }}
        columns={columns}
        jobsErrorMessage="Failed to get holiday import error data"
        recordsErrorMessage="Failed to get holiday import error data"
      />
    </>
  );
}
